import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select } from '../../../shared/components/ui';
import { menuService } from '../services/menuService';
import authService from '../../auth/services/authService';
import { useTranslation } from 'react-i18next';

const PreferencesWizard = ({ onMenuGenerated, onStartMenuGeneration, setLoading, loadUserPreferences = false }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        numPersonas: 2,
        alergias: [],
        preferencias: [],
        presupuesto: 'medio',
        complejidad: 'media',
        objetivo: 'saludable'
    });
    const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

    const totalSteps = 6;

    // Cargar preferencias del usuario si está logueado
    useEffect(() => {
        const loadPreferences = async () => {
            if (loadUserPreferences && authService.isAuthenticated()) {
                setIsLoadingPreferences(true);
                try {
                    const response = await menuService.getPreferences();
                    if (response && response.preferences) {
                        const prefs = response.preferences;
                        setFormData({
                            numPersonas: prefs.num_people || 2,
                            alergias: prefs.allergies || [],
                            preferencias: prefs.dietary_preferences || [],
                            presupuesto: prefs.budget_level || 'medio',
                            complejidad: prefs.complexity || 'media',
                            objetivo: prefs.goal || 'saludable'
                        });

                        // Actualizar localStorage también para mantener sincronizado
                        const userDataStr = localStorage.getItem('user_data');
                        if (userDataStr) {
                            try {
                                const userData = JSON.parse(userDataStr);
                                userData.preferences = prefs;
                                localStorage.setItem('user_data', JSON.stringify(userData));
                            } catch (error) {
                            }
                        }
                    }
                } catch (error) {
                    // Fallback a localStorage si falla la API
                    const userDataStr = localStorage.getItem('user_data');
                    if (userDataStr) {
                        try {
                            const userData = JSON.parse(userDataStr);
                            if (userData.preferences) {
                                setFormData({
                                    numPersonas: userData.preferences.num_people || 2,
                                    alergias: userData.preferences.allergies || [],
                                    preferencias: userData.preferences.dietary_preferences || [],
                                    presupuesto: userData.preferences.budget_level || 'medio',
                                    complejidad: userData.preferences.complexity || 'media',
                                    objetivo: userData.preferences.goal || 'saludable'
                                });
                            }
                        } catch (error) {
                        }
                    }
                } finally {
                    setIsLoadingPreferences(false);
                }
            }
        };

        loadPreferences();
    }, [loadUserPreferences]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTagInput = (field, value, setTempInput) => {
        if (value.trim() && value.includes(',')) {
            const newTags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field], ...newTags]
            }));
            setTempInput('');
        }
    };

    const handleKeyPress = (e, field, tempInput, setTempInput) => {
        if (e.key === 'Enter' && tempInput.trim()) {
            e.preventDefault();
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field], tempInput.trim()]
            }));
            setTempInput('');
        }
    };

    const removeTag = (field, indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, index) => index !== indexToRemove)
        }));
    };

    const nextStep = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);

        const preferencesData = {
            numPeople: parseInt(formData.numPersonas, 10),
            allergies: formData.alergias,
            preferences: formData.preferencias,
            budget: formData.presupuesto,
            complexity: formData.complejidad,
            goal: formData.objetivo,
        };

        try {
            await onMenuGenerated(preferencesData);
        } catch (error) {
            alert(t('menu.errors.generationFailed'));
        } finally {
            setLoading(false);
        }
    };

    const isStepValid = () => {
        switch (currentStep) {
            case 1:
                return formData.numPersonas > 0;
            case 2:
                return true; // Alergias es opcional
            case 3:
                return true; // Preferencias es opcional
            case 4:
                return formData.presupuesto;
            case 5:
                return formData.complejidad;
            case 6:
                return formData.objetivo;
            default:
                return false;
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('preferences.people.label')}</h2>
                            <p className="text-base sm:text-lg text-gray-600"></p>
                        </div>

                        <div className="flex justify-center px-4">
                            <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-md w-full">
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                    <button
                                        key={num}
                                        onClick={() => handleInputChange('numPersonas', num)}
                                        className={`aspect-square rounded-full text-lg sm:text-xl font-bold transition-all duration-200 min-h-[44px] ${formData.numPersonas === num
                                            ? 'bg-primary-600 text-white scale-110 shadow-lg'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
                                            }`}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="text-xs sm:text-sm text-gray-500">
                            {t('preferences.people.selected', { count: formData.numPersonas })}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('preferences.allergies.label')}</h2>
                            <p className="text-base sm:text-lg text-gray-600">{t('preferences.allergies.helper')}</p>
                        </div>

                        <div className="max-w-md mx-auto space-y-4 px-4">
                            <Input
                                type="text"
                                value={formData.alergiasInput || ''}
                                onChange={(e) => handleInputChange('alergiasInput', e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, 'alergias', formData.alergiasInput, (value) => handleInputChange('alergiasInput', value))}
                                placeholder={t('preferences.allergies.inputPlaceholder')}
                                className="text-center text-base sm:text-lg min-h-[44px]"
                            />

                            {formData.alergias.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {formData.alergias.map((alergia, index) => (
                                        <span
                                            key={index}
                                            className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                                        >
                                            {alergia}
                                            <button
                                                onClick={() => removeTag('alergias', index)}
                                                className="text-red-600 hover:text-red-800 font-bold"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="text-sm text-gray-500">
                            {formData.alergias.length > 0
                                ? t('preferences.allergies.count', { count: formData.alergias.length })
                                : t('preferences.allergies.none')}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('preferences.dietary.label')}</h2>
                            <p className="text-base sm:text-lg text-gray-600">{t('preferences.dietary.helper')}</p>
                        </div>

                        <div className="max-w-md mx-auto space-y-4 px-4">
                            <Input
                                type="text"
                                value={formData.preferenciasInput || ''}
                                onChange={(e) => handleInputChange('preferenciasInput', e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, 'preferencias', formData.preferenciasInput, (value) => handleInputChange('preferenciasInput', value))}
                                placeholder={t('preferences.dietary.inputPlaceholder')}
                                className="text-center text-base sm:text-lg min-h-[44px]"
                            />

                            {formData.preferencias.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {formData.preferencias.map((preferencia, index) => (
                                        <span
                                            key={index}
                                            className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                                        >
                                            {preferencia}
                                            <button
                                                onClick={() => removeTag('preferencias', index)}
                                                className="text-green-600 hover:text-green-800 font-bold"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="text-sm text-gray-500">
                            {formData.preferencias.length > 0
                                ? t('preferences.dietary.count', { count: formData.preferencias.length })
                                : t('preferences.dietary.none')}
                        </div>
                    </div>
                );

            case 4:
                const budgetOptions = [
                    { value: 'barato', label: '💰 Barato', description: 'Ingredientes económicos' },
                    { value: 'medio', label: '💳 Medio', description: 'Balance calidad-precio' },
                    { value: 'alto', label: '💎 Alto', description: 'Ingredientes premium' }
                ];

                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('menu.wizard.budget.title')}</h2>
                            <p className="text-base sm:text-lg text-gray-600"></p>
                        </div>

                        <div className="grid gap-3 sm:gap-4 max-w-lg mx-auto px-4">
                            {[
                                { value: 'barato', label: t('menu.wizard.budget.cheap'), description: t('menu.wizard.budget.cheapDesc') },
                                { value: 'medio', label: t('menu.wizard.budget.medium'), description: t('menu.wizard.budget.mediumDesc') },
                                { value: 'alto', label: t('menu.wizard.budget.high'), description: t('menu.wizard.budget.highDesc') }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleInputChange('presupuesto', option.value)}
                                    className={`p-4 sm:p-6 rounded-xl border-2 transition-all duration-200 text-left min-h-[60px] active:scale-95 ${formData.presupuesto === option.value
                                        ? 'border-primary-600 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                                        }`}
                                >
                                    <div className="text-lg sm:text-xl font-semibold">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">{option.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 5:
                const complexityOptions = [
                    { value: 'baja', label: '⚡ Baja', description: 'Platos simples y rápidos' },
                    { value: 'media', label: '⏱️ Media', description: 'Preparación moderada' },
                    { value: 'alta', label: '👨‍🍳 Alta', description: 'Platos elaborados' }
                ];

                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('menu.wizard.complexity.title')}</h2>
                            <p className="text-base sm:text-lg text-gray-600"></p>
                        </div>

                        <div className="grid gap-3 sm:gap-4 max-w-lg mx-auto px-4">
                            {[
                                { value: 'baja', label: t('menu.wizard.complexity.low'), description: t('menu.wizard.complexity.lowDesc') },
                                { value: 'media', label: t('menu.wizard.complexity.medium'), description: t('menu.wizard.complexity.mediumDesc') },
                                { value: 'alta', label: t('menu.wizard.complexity.high'), description: t('menu.wizard.complexity.highDesc') }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleInputChange('complejidad', option.value)}
                                    className={`p-4 sm:p-6 rounded-xl border-2 transition-all duration-200 text-left min-h-[60px] active:scale-95 ${formData.complejidad === option.value
                                        ? 'border-primary-600 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                                        }`}
                                >
                                    <div className="text-lg sm:text-xl font-semibold">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">{option.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 6:
                const objetivoOptions = [
                    { value: 'musculacion', label: '💪 Musculación', description: 'Ganancia de masa muscular' },
                    { value: 'adelgazar', label: '⚖️ Adelgazar', description: 'Pérdida de peso' },
                    { value: 'mantener', label: '🎯 Mantener', description: 'Mantenimiento del peso' },
                    { value: 'saludable', label: '🥗 Saludable', description: 'Alimentación equilibrada' }
                ];

                return (
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="space-y-3 sm:space-y-4 px-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('menu.wizard.goal.title')}</h2>
                            <p className="text-base sm:text-lg text-gray-600"></p>
                        </div>

                        <div className="grid gap-3 sm:gap-4 max-w-lg mx-auto px-4">
                            {[
                                { value: 'musculacion', label: t('menu.wizard.goal.muscle'), description: t('menu.wizard.goal.muscleDesc') },
                                { value: 'adelgazar', label: t('menu.wizard.goal.lose'), description: t('menu.wizard.goal.loseDesc') },
                                { value: 'mantener', label: t('menu.wizard.goal.maintain'), description: t('menu.wizard.goal.maintainDesc') },
                                { value: 'saludable', label: t('menu.wizard.goal.healthy'), description: t('menu.wizard.goal.healthyDesc') }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleInputChange('objetivo', option.value)}
                                    className={`p-4 sm:p-6 rounded-xl border-2 transition-all duration-200 text-left min-h-[60px] active:scale-95 ${formData.objetivo === option.value
                                        ? 'border-primary-600 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                                        }`}
                                >
                                    <div className="text-lg sm:text-xl font-semibold">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">{option.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl mx-auto" shadow="lg">
                {/* Progress Bar */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-gray-600">
                            {t('common.progress.stepOf', { current: currentStep, total: totalSteps })}
                        </span>
                        <span className="text-sm font-medium text-primary-600">
                            {t('common.progress.percentCompleted', { percent: Math.round((currentStep / totalSteps) * 100) })}
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Step Content */}
                <div className="min-h-[400px] flex flex-col justify-center">
                    {renderStep()}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 gap-3">
                    <Button
                        variant="outline"
                        onClick={prevStep}
                        disabled={currentStep === 1}
                        className="px-4 sm:px-6 min-h-[44px] text-sm sm:text-base"
                    >
                        <span className="hidden sm:inline">← {t('common.previous')}</span><span className="sm:hidden">←</span>
                    </Button>

                    {currentStep < totalSteps ? (
                        <Button
                            variant="primary"
                            onClick={nextStep}
                            disabled={!isStepValid()}
                            className="px-4 sm:px-6 min-h-[44px] text-sm sm:text-base"
                        >
                            <span className="hidden sm:inline">{t('common.next')} →</span><span className="sm:hidden">→</span>
                        </Button>
                    ) : (
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end flex-1 ml-auto">
                            <Button
                                variant="success"
                                onClick={handleSubmit}
                                disabled={!isStepValid()}
                                size="lg"
                                className="px-6 sm:px-8 min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
                            >
                                <span className="hidden sm:inline">{t('preferences.save')}</span><span className="sm:hidden">{t('preferences.save')}</span>
                            </Button>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default PreferencesWizard;
